/**
 * useNotificationStore — unread badge + inbox list for the Header Bell.
 *
 * Polls unread count every 45s while the app is authenticated. The panel
 * refreshes the list on open. Mark-read is optimistic so the badge drops
 * immediately.
 */

import { create } from 'zustand';
import {
    notificationService,
    type InboxNotification,
    type NotificationCategory,
} from '../services/api';

export type NotificationFilter = 'all' | 'unread';

const POLL_MS = 45_000;

interface NotificationState {
    items: InboxNotification[];
    unreadCount: number;
    loading: boolean;
    error: string | null;
    filter: NotificationFilter;
    startPolling: () => void;
    stopPolling: () => void;
    fetchUnreadCount: () => Promise<void>;
    fetchList: () => Promise<void>;
    setFilter: (filter: NotificationFilter) => void;
    markRead: (id: number) => Promise<void>;
    markAllRead: () => Promise<void>;
}

let pollTimer: ReturnType<typeof setInterval> | null = null;

function hasToken(): boolean {
    try {
        return Boolean(localStorage.getItem('token'));
    } catch {
        return false;
    }
}

export function hrefForNotification(n: InboxNotification): string {
    switch (n.category as NotificationCategory | string) {
        case 'watchlist_alert':
            return n.case_id ? `/cases/${n.case_id}` : '/watchlist?tab=events';
        case 'feedback_reply':
            return '/feedback';
        case 'case_shared':
        case 'auto_case':
            return n.case_id ? `/cases/${n.case_id}` : '/cases';
        case 'import_complete':
            return n.case_id ? `/cases/${n.case_id}` : '/watchlist';
        case 'scraper_run':
            return '/data-sources';
        default:
            return '/';
    }
}

export const useNotificationStore = create<NotificationState>((set, get) => ({
    items: [],
    unreadCount: 0,
    loading: false,
    error: null,
    filter: 'all',

    startPolling: () => {
        if (pollTimer) return;
        void get().fetchUnreadCount();
        pollTimer = setInterval(() => {
            void get().fetchUnreadCount();
        }, POLL_MS);
    },

    stopPolling: () => {
        if (pollTimer) {
            clearInterval(pollTimer);
            pollTimer = null;
        }
    },

    fetchUnreadCount: async () => {
        if (!hasToken()) {
            set({ unreadCount: 0 });
            return;
        }
        try {
            const data = await notificationService.unreadCount();
            set({ unreadCount: data.unread_count ?? 0, error: null });
        } catch {
            // Stay silent on poll failures — don't flash errors on the header.
        }
    },

    fetchList: async () => {
        if (!hasToken()) {
            set({ items: [], unreadCount: 0, loading: false });
            return;
        }
        set({ loading: true, error: null });
        try {
            const unread = get().filter === 'unread';
            const data = await notificationService.list({ limit: 40, unread });
            set({
                items: data.items || [],
                unreadCount: data.unread_count ?? 0,
                loading: false,
                error: null,
            });
        } catch {
            set({ loading: false, error: 'Could not load notifications' });
        }
    },

    setFilter: (filter) => {
        set({ filter });
        void get().fetchList();
    },

    markRead: async (id) => {
        const prev = get().items;
        const wasUnread = prev.find(n => n.id === id)?.unread;
        set({
            items: prev.map(n => n.id === id ? { ...n, unread: false, read_at: n.read_at || new Date().toISOString() } : n),
            unreadCount: Math.max(0, get().unreadCount - (wasUnread ? 1 : 0)),
        });
        try {
            await notificationService.markRead(id);
        } catch {
            set({ items: prev, unreadCount: get().unreadCount + (wasUnread ? 1 : 0) });
        }
    },

    markAllRead: async () => {
        const prev = get().items;
        const prevCount = get().unreadCount;
        set({
            items: prev.map(n => ({ ...n, unread: false, read_at: n.read_at || new Date().toISOString() })),
            unreadCount: 0,
        });
        try {
            await notificationService.markAllRead();
        } catch {
            set({ items: prev, unreadCount: prevCount });
        }
    },
}));
